/* **The ICON button** — a square, outlined control whose whole label is its glyph. The fourth of
 * this app's button treatments, beside `emberButton`, `inkButton` and `ghostButton`.
 *
 * A stylesheet with no component, for the reason `ghostButton.module.css` gives at length: a
 * component would have to take the geometry as props, and the surfaces that draw this control draw
 * it at four sizes already — the programator's 26px step arrow, its 28px ✕ and host toggle, and the
 * Ankiety tab's 26px compare, step and close controls. That is a prop bag around a `<button>`.
 *
 * ── What is here, and what stays on the surface — `ghostButton`'s contract exactly ───────────
 * Two classes on the element: this one for the TREATMENT — the ink, the hairline edge, the cursor,
 * the enlarged hit target and the three states — and the surface's own for the GEOMETRY, declared
 * there as ordinary properties:
 *
 *   width          height          border-radius
 *   font-size      font-weight     background
 *
 * `background` is the surface's for the same reason it is on the ghost: an icon button on a panel
 * head is transparent and one inside a filled chip is not, so the fill is a fact about where the
 * control stands.
 *
 * **Nothing may be declared on both sides.** A declaration living in a shared module and in a
 * surface's own is settled by CHUNK ORDER, which differs between the dev server and the build.
 * Where a surface genuinely has to override one of the properties below — the programator's ✕ takes
 * a lighter edge and a quieter ink — it does so from a TWO-CLASS selector (`.iconButton.closeButton`)
 * so specificity decides rather than the bundler.
 *
 * ── The hit target grows and the painted box does not ────────────────────────────────────────
 * These are drawn at 26–28px, under the 30px pointer floor. `--hit-extend` is a negative inset on a
 * pseudo-element, so the target reaches the floor without the control changing size.
 *
 * ── A refused control is DIMMED and stays focusable ──────────────────────────────────────────
 * `opacity: .35`, the edge and the ink left alone, so it still reads as the same control — which is
 * what makes "there is no previous occurrence" legible rather than looking like a different button.
 * `aria-disabled` is styled beside the real attribute because the two mean different things and a
 * control carrying a reason has to stay in the tab order for the reason to be reachable at all.
 *
 * Flat, no shadow, no glow, nothing moves in Z. */

.iconButton {
  position: relative;
  display: grid;
  flex: none;
  place-items: center;
  padding: var(--space-0);
  line-height: var(--lh-none);
  color: var(--text-2);
  cursor: pointer;
  border: var(--border-hairline) solid var(--border-2);
}

.iconButton::after {
  position: absolute;
  inset: var(--hit-extend);
  content: "";
}

.iconButton:hover {
  color: var(--text);
  border-color: var(--text-2);
}

.iconButton:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

.iconButton[aria-disabled='true'],
.iconButton:disabled {
  cursor: default;
  opacity: 0.35;
}
